import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../../lib/firebase';
import { useNavigate } from 'react-router-dom';
import {
  Tag,
  Settings,
  Plus,
  Save,
  AlertCircle,
  Check,
  Edit2,
  Eye,
  EyeOff,
  Clock,
  ArrowUp,
  Users,
  TrendingUp,
  BarChart3,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useToast } from '../../../hooks/use-toast';
import { useBusiness } from '../../../contexts/BusinessContext';
import { getErrorMessage } from '../../../lib/errorMessage';
import { useLanguage } from '../../../contexts/LanguageContext';
import { tenantHeaders } from '../../../lib/tenantSession';


const BusinessCategoryManager = () => {
  const { currentBusiness } = useBusiness();
  const navigate = useNavigate();
  const [businessCategories, setBusinessCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddCustomDialog, setShowAddCustomDialog] = useState(false);
  const [industryInfo, setIndustryInfo] = useState({ industry: '', subSector: '' });
  
  // Edit form state (for customizing inherited categories)
  const [editForm, setEditForm] = useState({
    custom_priority: '',
    custom_escalation_level: '',
    custom_sla_hours: '',
    custom_keywords: '',
    is_enabled: true
  });
  
  // Custom category form state (for creating new business-specific categories)
  const [customCategoryForm, setCustomCategoryForm] = useState({
    name: '',
    description: '',
    priority_level: 'medium',
    escalation_level: 'operator',
    sla_hours: '24',
    ai_keywords: ''
  });
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    enabled: 0,
    disabled: 0,
    customized: 0,
    inherited: 0,
    custom: 0
  });
  
  const { toast } = useToast();
  const { t } = useLanguage();

  // Obtain the CURRENT Firebase user's fresh ID token immediately before each
  // protected request. Returns null when auth is not ready — callers MUST stop
  // (never send `Bearer null`). Deliberately NO localStorage fallback: a cached
  // bearer can be expired or belong to a previous session (the cause of the 401).
  const getFreshToken = async () => {
    if (!auth.currentUser) return null;
    return await auth.currentUser.getIdToken();
  };

  useEffect(() => {
    const brandId = currentBusiness?.brandId;
    if (brandId) {
      fetchBusinessCategories(brandId);
    } else {
      setLoading(false);
      setBusinessCategories([]);
      setIndustryInfo({ industry: t('pcb.category.notSet'), subSector: t('pcb.category.notSet') });
    }
  }, [currentBusiness?.brandId]);

  const fetchBusinessCategories = async (businessId) => {
    setLoading(true);
    try {
      const token = await getFreshToken();
      if (!token) {
        setLoading(false);
        setBusinessCategories([]);
        toast({ title: t('pg.category.authNotReady'), description: t('pg.category.refreshTryAgain'), variant: 'destructive' });
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(
        `${backendUrl}/api/v1/business/${businessId}/feedback-categories`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      
      console.log('Category Manager Response:', response.data);
      
      const data = response.data;
      
      // Handle different response structures
      let categories = [];
      if (Array.isArray(data)) {
        categories = data;
      } else if (data.categories && Array.isArray(data.categories)) {
        categories = data.categories;
      } else if (data.feedback_categories && Array.isArray(data.feedback_categories)) {
        categories = data.feedback_categories;
      }
      
      setBusinessCategories(categories);
      setIndustryInfo({
        industry: data.industry_name || data.industry || t('pcb.category.notSet'),
        subSector: data.sub_sector_name || data.subsector || t('pcb.category.notSet')
      });
      
      // Calculate stats
      setStats({
        total: categories.length,
        enabled: categories.filter(c => c.is_enabled !== false).length,
        disabled: categories.filter(c => c.is_enabled === false).length,
        customized: categories.filter(c => c.custom_priority || c.custom_escalation_level || c.custom_keywords?.length > 0).length,
        inherited: categories.filter(c => !c.is_custom).length,
        custom: categories.filter(c => c.is_custom).length
      });
      
    } catch (error) {
      console.error('Error fetching business categories:', error);
      console.error('Error details:', error.response?.data);
      
      // Set empty state on error
      setBusinessCategories([]);
      setIndustryInfo({
        industry: t('pcb.category.notSet'),
        subSector: t('pcb.category.notSet')
      });

      toast({
        title: t('common.error'),
        description: getErrorMessage(error, t('pg.category.failedLoadCategories')),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setEditForm({
      custom_priority: category.custom_priority || '',
      custom_escalation_level: category.custom_escalation_level || '',
      custom_sla_hours: category.custom_sla_hours || '',
      custom_keywords: (category.custom_keywords || []).join(', '),
      is_enabled: category.is_enabled !== false
    });
    setShowEditDialog(true);
  };

  const handleSaveCustomization = async () => {
    try {
      const payload = {
        ...editForm,
        custom_keywords: editForm.custom_keywords
          .split(',')
          .map(k => k.trim())
          .filter(k => k)
      };

      const token = await getFreshToken();
      if (!token) {
        toast({ title: t('pg.category.authNotReady'), description: t('pg.category.refreshTryAgain'), variant: 'destructive' });
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.patch(
        `${backendUrl}/api/v1/business/${currentBusiness.brandId}/categories/${selectedCategory.business_category_id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );

      toast({
        title: t('pcb.common.success'),
        description: t('pg.category.customizationSaved')
      });

      setShowEditDialog(false);
      if (currentBusiness?.brandId) fetchBusinessCategories(currentBusiness.brandId);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: getErrorMessage(error, t('pcb.category.failedSaveCustomization')),
        variant: 'destructive'
      });
    }
  };

  const handleAddCustomCategory = async () => {
    try {
      const businessId = currentBusiness?.brandId;

      const payload = {
        business_id: businessId,
        name: customCategoryForm.name,
        description: customCategoryForm.description,
        default_priority_level: customCategoryForm.priority_level,
        default_escalation_level: customCategoryForm.escalation_level,
        default_sla_hours: parseInt(customCategoryForm.sla_hours),
        ai_intent_keywords: customCategoryForm.ai_keywords
          .split(',')
          .map(k => k.trim())
          .filter(k => k),
        is_custom: true
      };

      const token = await getFreshToken();
      if (!token) {
        toast({ title: t('pg.category.authNotReady'), description: t('pg.category.refreshTryAgain'), variant: 'destructive' });
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(
        `${backendUrl}/api/v1/business/${businessId}/custom-categories`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );

      toast({
        title: t('pcb.common.success'),
        description: t('pcb.category.customCreated', { name: customCategoryForm.name })
      });

      setShowAddCustomDialog(false);
      setCustomCategoryForm({
        name: '',
        description: '',
        priority_level: 'medium',
        escalation_level: 'operator',
        sla_hours: '24',
        ai_keywords: ''
      });
      if (businessId) fetchBusinessCategories(businessId);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: getErrorMessage(error, t('pcb.category.failedCreateCustom')),
        variant: 'destructive'
      });
    }
  };

  // Durable enable/disable via the canonical PATCH override (await backend, then refetch —
  // never optimistic/local success). Works for custom AND inherited/universal rows.
  const handleToggleEnabled = async (category) => {
    try {
      const token = await getFreshToken();
      if (!token) {
        toast({ title: t('pg.category.authNotReady'), description: t('pg.category.refreshTryAgain'), variant: 'destructive' });
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const nextEnabled = !(category.is_enabled !== false);
      await axios.patch(
        `${backendUrl}/api/v1/business/${currentBusiness.brandId}/categories/${category.business_category_id}`,
        { is_enabled: nextEnabled },
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      toast({ title: t('pcb.common.success'), description: nextEnabled ? t('pcb.category.categoryEnabled') : t('pcb.category.categoryDisabled') });
      if (currentBusiness?.brandId) fetchBusinessCategories(currentBusiness.brandId);
    } catch (error) {
      toast({ title: t('common.error'), description: getErrorMessage(error, t('pcb.category.failedUpdate')), variant: 'destructive' });
    }
  };

  // Durable DELETE of a tenant CUSTOM category via the canonical route. Inherited/universal
  // cannot be deleted (backend returns 409) — they are disabled via is_enabled instead.
  // Awaits backend confirmation, then refetches; surfaces real errors (no fake success).
  const handleDeleteCategory = async (category) => {
    if (!category.is_custom) {
      toast({ title: t('pcb.category.cannotDelete'), description: t('pcb.category.inheritedCannotDelete'), variant: 'destructive' });
      return;
    }
    if (!window.confirm(t('pcb.category.confirmDelete', { name: category.name }))) return;
    try {
      const token = await getFreshToken();
      if (!token) {
        toast({ title: t('pg.category.authNotReady'), description: t('pg.category.refreshTryAgain'), variant: 'destructive' });
        return;
      }
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.delete(
        `${backendUrl}/api/v1/business/${currentBusiness.brandId}/categories/${category.business_category_id}`,
        { headers: { Authorization: `Bearer ${token}`, ...tenantHeaders() } }
      );
      toast({ title: t('pcb.category.deleted'), description: t('pcb.category.customRemoved', { name: category.name }) });
      if (currentBusiness?.brandId) fetchBusinessCategories(currentBusiness.brandId);
    } catch (error) {
      const code = error.response?.status;
      const msg = code === 409 ? t('pcb.category.cannotDeleteInherited')
        : code === 404 ? t('pcb.category.notFound')
        : (getErrorMessage(error, t('pcb.category.failedDelete')));
      toast({ title: t('common.error'), description: msg, variant: 'destructive' });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'destructive';
      case 'high':
        return 'default';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'secondary';
    }
  };

  const getEscalationIcon = (level) => {
    switch (level?.toLowerCase()) {
      case 'admin':
        return <ArrowUp className="h-4 w-4 text-red-600" />;
      case 'manager':
        return <ArrowUp className="h-4 w-4 text-orange-600" />;
      case 'operator':
        return <Users className="h-4 w-4 text-blue-600" />;
      default:
        return <Users className="h-4 w-4 text-gray-600" />;
    }
  };

  const displayPriority = (category) => {
    return category.custom_priority || category.default_priority_level || 'medium';
  };

  const displayEscalation = (category) => {
    return category.custom_escalation_level || 'operator';
  };

  const displaySLA = (category) => {
    return category.custom_sla_hours || 24;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('pcb.category.loadingCategories')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('pg.category.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('pg.category.subtitle')}
          </p>
          <div className="flex gap-4 mt-2 text-sm text-gray-600">
            <span><strong>{t('pcb.category.industry')}:</strong> {industryInfo.industry}</span>
            <span><strong>{t('pcb.category.subSector')}:</strong> {industryInfo.subSector}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowAddCustomDialog(true)}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('pcb.category.addCustom')}
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate('/business/analytics')}>
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('pcb.category.viewAnalytics')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pcb.category.totalCategories')}</p>
                <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <Tag className="h-8 w-8 text-blue-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pcb.category.enabled')}</p>
                <p className="text-3xl font-bold text-green-600">{stats.enabled}</p>
              </div>
              <Check className="h-8 w-8 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pcb.category.customized')}</p>
                <p className="text-3xl font-bold text-purple-600">{stats.customized}</p>
              </div>
              <Settings className="h-8 w-8 text-purple-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pcb.category.inherited')}</p>
                <p className="text-3xl font-bold text-orange-600">{stats.inherited}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-orange-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pcb.category.universal')}</p>
                <p className="text-3xl font-bold text-gray-600">
                  {businessCategories.filter(c => c.source_type === 'universal').length}
                </p>
              </div>
              <AlertCircle className="h-8 w-8 text-gray-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pcb.category.custom')}</p>
                <p className="text-3xl font-bold text-green-600">{stats.custom}</p>
              </div>
              <Tag className="h-8 w-8 text-green-600 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Categories List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('pcb.category.activeCategories')}</CardTitle>
          <CardDescription>
            {t('pcb.category.activeCategoriesDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {businessCategories.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">{t('pcb.category.noCategories')}</p>
              <p className="text-sm text-gray-500">
                {t('pcb.category.noCategoriesHint')}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {businessCategories.map((category) => {
                const hasCustomization = category.custom_priority || 
                                        category.custom_escalation_level || 
                                        (category.custom_keywords && category.custom_keywords.length > 0);
                const isCustom = category.is_custom;
                
                return (
                  <div
                    key={category.id}
                    className={`border rounded-lg p-4 hover:border-blue-300 transition-colors ${
                      isCustom ? 'bg-green-50 border-green-200' : 
                      hasCustomization ? 'bg-blue-50 border-blue-200' : 'bg-white'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {isCustom ? (
                            <Tag className="h-5 w-5 text-green-600" />
                          ) : category.source_type === 'universal' ? (
                            <Check className="h-5 w-5 text-orange-600" />
                          ) : (
                            <Tag className="h-5 w-5 text-blue-600" />
                          )}
                          <h3 className="font-semibold text-gray-900 text-lg">{category.name}</h3>
                          <Badge variant={getPriorityColor(displayPriority(category))}>
                            {displayPriority(category)}
                          </Badge>
                          {isCustom && (
                            <Badge variant="outline" className="text-green-600 border-green-600">
                              {t('pcb.category.custom')}
                            </Badge>
                          )}
                          {category.source_type === 'universal' && (
                            <Badge variant="outline" className="text-orange-600 border-orange-600">
                              {t('pcb.category.universal')}
                            </Badge>
                          )}
                          {hasCustomization && (
                            <Badge variant="outline" className="text-purple-600 border-purple-600">
                              {t('pcb.category.customized')}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                        
                        <div className="grid grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-2">
                            {getEscalationIcon(displayEscalation(category))}
                            <div>
                              <span className="text-gray-500">{t('pcb.category.escalation')}</span>
                              <span className="ml-2 font-medium capitalize">
                                {displayEscalation(category)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-blue-600" />
                            <div>
                              <span className="text-gray-500">{t('pcb.category.sla')}</span>
                              <span className="ml-2 font-medium">{displaySLA(category)}h</span>
                            </div>
                          </div>
                          <div>
                            <span className="text-gray-500">{t('pcb.category.aiKeywords')}</span>
                            <span className="ml-2 font-medium">{category.all_keywords?.length || 0}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">{t('pcb.category.customKeywords')}</span>
                            <span className="ml-2 font-medium text-purple-600">
                              {category.custom_keywords?.length || 0}
                            </span>
                          </div>
                        </div>
                        
                        {/* Show some keywords */}
                        <div className="mt-3 flex flex-wrap gap-1">
                          {(category.all_keywords || []).slice(0, 5).map((keyword, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded"
                            >
                              {keyword}
                            </span>
                          ))}
                          {(category.all_keywords || []).length > 5 && (
                            <span className="text-xs text-gray-500 px-2 py-1">
                              {t('pcb.category.moreCount', { count: category.all_keywords.length - 5 })}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        {category.is_enabled !== false ? (
                          <Eye className="h-4 w-4 text-green-500" title={t('pcb.category.enabled')} />
                        ) : (
                          <EyeOff className="h-4 w-4 text-gray-400" title={t('pcb.category.disabled')} />
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleEnabled(category)}
                          title={category.is_enabled !== false ? t('pcb.category.disable') : t('pcb.category.enable')}
                        >
                          {category.is_enabled !== false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditCategory(category)}
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          {t('pcb.category.customize')}
                        </Button>
                        {category.is_custom && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={() => handleDeleteCategory(category)}
                            title={t('pcb.category.deleteCustomTitle')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Category Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('pcb.category.customizeCategory')}: {selectedCategory?.name}</DialogTitle>
            <DialogDescription>
              {t('pcb.category.editDialogDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>{t('pcb.category.note')}:</strong> {t('pcb.category.editNote')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="edit-custom-priority" className="block text-sm font-medium mb-2">{t('pcb.category.customPriority')}</label>
                <Select
                  value={editForm.custom_priority || '__default__'}
                  onValueChange={(value) =>
                    setEditForm({ ...editForm, custom_priority: value === '__default__' ? '' : value })
                  }
                >
                  <SelectTrigger id="edit-custom-priority">
                    <SelectValue placeholder={`${t('pcb.category.defaultLabel')}: ${selectedCategory?.default_priority_level}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">{t('pcb.category.useDefault')}</SelectItem>
                    <SelectItem value="low">{t('pcb.category.low')}</SelectItem>
                    <SelectItem value="medium">{t('pcb.category.medium')}</SelectItem>
                    <SelectItem value="high">{t('pcb.category.high')}</SelectItem>
                    <SelectItem value="critical">{t('pcb.category.critical')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {t('pcb.category.leaveEmptyDefault')}: {selectedCategory?.default_priority_level}
                </p>
              </div>
              
              <div>
                <label htmlFor="edit-custom-escalation" className="block text-sm font-medium mb-2">{t('pcb.category.customEscalation')}</label>
                <Select
                  value={editForm.custom_escalation_level || '__default__'}
                  onValueChange={(value) =>
                    setEditForm({ ...editForm, custom_escalation_level: value === '__default__' ? '' : value })
                  }
                >
                  <SelectTrigger id="edit-custom-escalation">
                    <SelectValue placeholder={t('pcb.category.setEscalation')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__default__">{t('pcb.category.useDefault')}</SelectItem>
                    <SelectItem value="operator">{t('role.operator')}</SelectItem>
                    <SelectItem value="manager">{t('role.manager')}</SelectItem>
                    <SelectItem value="admin">{t('role.admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label htmlFor="edit-custom-sla" className="block text-sm font-medium mb-2">
                {t('pcb.category.customSlaHours')}
              </label>
              <Input
                id="edit-custom-sla"
                type="number"
                placeholder={t('pcb.category.egSla24')}
                value={editForm.custom_sla_hours}
                onChange={(e) =>
                  setEditForm({ ...editForm, custom_sla_hours: e.target.value })
                }
                min="1"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('pcb.category.slaHint')}
              </p>
            </div>
            
            <div>
              <label htmlFor="edit-ai-keywords" className="block text-sm font-medium mb-2">
                {t('pcb.category.additionalKeywords')}
              </label>
              <Textarea
                id="edit-ai-keywords"
                placeholder={t('pcb.category.keywordsPlaceholder')}
                value={editForm.custom_keywords}
                onChange={(e) =>
                  setEditForm({ ...editForm, custom_keywords: e.target.value })
                }
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('pcb.category.keywordsHint')}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="font-medium text-sm mb-2">{t('pcb.category.defaultAiKeywords')}</h4>
              <div className="flex flex-wrap gap-1">
                {(selectedCategory?.ai_intent_keywords || []).map((keyword, idx) => (
                  <span
                    key={idx}
                    className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t('pcb.common.cancel')}
            </Button>
            <Button onClick={handleSaveCustomization}>
              <Save className="h-4 w-4 mr-2" />
              {t('pcb.category.saveCustomization')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Custom Category Dialog */}
      <Dialog open={showAddCustomDialog} onOpenChange={setShowAddCustomDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('pcb.category.createTitle')}</DialogTitle>
            <DialogDescription>
              {t('pcb.category.createDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label htmlFor="new-category-name" className="block text-sm font-medium mb-2">{t('pcb.category.categoryName')} *</label>
              <Input
                id="new-category-name"
                placeholder={t('pcb.category.namePlaceholder')}
                value={customCategoryForm.name}
                onChange={(e) => setCustomCategoryForm({ ...customCategoryForm, name: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="new-category-desc" className="block text-sm font-medium mb-2">{t('pcb.category.description')}</label>
              <Textarea
                id="new-category-desc"
                placeholder={t('pcb.category.descPlaceholder')}
                value={customCategoryForm.description}
                onChange={(e) => setCustomCategoryForm({ ...customCategoryForm, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label htmlFor="new-priority-level" className="block text-sm font-medium mb-2">{t('pcb.category.priorityLevel')}</label>
                <Select
                  value={customCategoryForm.priority_level}
                  onValueChange={(value) => setCustomCategoryForm({ ...customCategoryForm, priority_level: value })}
                >
                  <SelectTrigger id="new-priority-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('pcb.category.low')}</SelectItem>
                    <SelectItem value="medium">{t('pcb.category.medium')}</SelectItem>
                    <SelectItem value="high">{t('pcb.category.high')}</SelectItem>
                    <SelectItem value="critical">{t('pcb.category.critical')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="new-escalation-level" className="block text-sm font-medium mb-2">{t('pcb.category.escalateTo')}</label>
                <Select
                  value={customCategoryForm.escalation_level}
                  onValueChange={(value) => setCustomCategoryForm({ ...customCategoryForm, escalation_level: value })}
                >
                  <SelectTrigger id="new-escalation-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">{t('role.operator')}</SelectItem>
                    <SelectItem value="manager">{t('role.manager')}</SelectItem>
                    <SelectItem value="admin">{t('role.admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="new-sla-hours" className="block text-sm font-medium mb-2">{t('pcb.category.slaHours')}</label>
                <Input
                  id="new-sla-hours"
                  type="number"
                  placeholder="24"
                  value={customCategoryForm.sla_hours}
                  onChange={(e) => setCustomCategoryForm({ ...customCategoryForm, sla_hours: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label htmlFor="new-ai-keywords" className="block text-sm font-medium mb-2">
                {t('pcb.category.aiKeywordsCsv')}
              </label>
              <Textarea
                id="new-ai-keywords"
                placeholder={t('pcb.category.keywordsPlaceholder2')}
                value={customCategoryForm.ai_keywords}
                onChange={(e) => setCustomCategoryForm({ ...customCategoryForm, ai_keywords: e.target.value })}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('pcb.category.keywordsHint2')}
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddCustomDialog(false)}>
              {t('pcb.common.cancel')}
            </Button>
            <Button
              onClick={handleAddCustomCategory}
              disabled={!customCategoryForm.name}
              className="bg-green-600 hover:bg-green-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {t('pcb.category.createCategory')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessCategoryManager;